import { Box } from "@mui/material";
import { useParams } from "react-router";
import { DotLegalButton, DotLegalEmptyState, DotLegalHeader, DotLegalPaper } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { RiskAreaMatrix } from "../../riskSettings/riskArea/riskAreaDialog/riskAreaMatrix/RiskAreaMatrix";
import RiskGovernanceFilterBar from "./RiskGovernanceFilterBar";
import RiskGovernanceStatRow from "./RiskGovernanceStatRow";
import RiskGovernanceEntityTable from "./RiskGovernanceEntityTable";
import RiskDistributionHistoryChart from "./riskDistributionHistoryChart/RiskDistributionHistoryChart";
import RiskSnapshotDialog from "./RiskSnapshotDialog";
import { useRiskGovernanceOverview } from "./RiskGovernanceOverview.hooks";
import { useRiskGovernanceOverviewStyles } from "./RiskGovernanceOverview.styles";

function RiskGovernanceOverview() {
    const styles = useRiskGovernanceOverviewStyles();
    const { translateString } = useTranslation();
    const { riskAreaId } = useParams<{ riskAreaId: string }>();

    const {
        isLoading,
        hasData,
        scale,
        appetite,
        assessedCount,
        assessedTotalLabel,
        controlCoverageLabel,
        notAssessedCount,
        assessedProgress,
        overAppetiteCount,
        untreatedOverAppetiteCount,
        overduePlans,
        plansWithoutLinkedControls,
        controlCoverage,
        linkedRiskOverAppetiteCount,
        overAppetiteUrl,
        untreatedOverAppetiteUrl,
        linkedRiskOverAppetiteUrl,
        overduePlansUrl,
        plansWithoutLinkedControlsUrl,
        notFullyCoveredUrl,
        notAssessedUrl,
        exposureTile,
        showExposureTile,
        consequenceLevels,
        probabilityLevels,
        riskLevelIndicators,
        riskLevels,
        matrix,
        cellCounts,
        cellMovements,
        distributionHistory,
        distributionHistoryLoading,
        cellEntities,
        selectedCell,
        selectedCellLabel,
        onCellClick,
        clearCellFilter,
        getRiskChipStyle,
        navigateToEntity,
        getEntityUrl,
        filterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        searchText,
        onSearchTextChange,
        hasActiveFilters,
        clearFilter,
        mode,
        onModeChange,
        isAsOf,
        afterMitigation,
        asOf,
        snapshotDialogOpen,
        snapshotDates,
        snapshotDatesLoading,
        onOpenSnapshotDialog,
        onCloseSnapshotDialog,
        onSelectSnapshotDate,
        onReturnToToday,
        historyLink,
    } = useRiskGovernanceOverview(riskAreaId);

    return (
        <Box sx={styles.container}>
            <RiskGovernanceStatRow
                isLoading={isLoading}
                isAsOf={isAsOf}
                afterMitigation={afterMitigation}
                showExposureTile={showExposureTile}
                assessedCount={assessedCount}
                assessedTotalLabel={assessedTotalLabel}
                controlCoverageLabel={controlCoverageLabel}
                notAssessedCount={notAssessedCount}
                assessedProgress={assessedProgress}
                appetite={appetite}
                overAppetiteCount={overAppetiteCount}
                untreatedOverAppetiteCount={untreatedOverAppetiteCount}
                linkedRiskOverAppetiteCount={linkedRiskOverAppetiteCount}
                overduePlans={overduePlans}
                plansWithoutLinkedControls={plansWithoutLinkedControls}
                controlCoverage={controlCoverage}
                overAppetiteUrl={overAppetiteUrl}
                untreatedOverAppetiteUrl={untreatedOverAppetiteUrl}
                linkedRiskOverAppetiteUrl={linkedRiskOverAppetiteUrl}
                overduePlansUrl={overduePlansUrl}
                plansWithoutLinkedControlsUrl={plansWithoutLinkedControlsUrl}
                notFullyCoveredUrl={notFullyCoveredUrl}
                notAssessedUrl={notAssessedUrl}
                exposureTile={exposureTile}
            />

            <RiskGovernanceFilterBar
                isLoading={isLoading}
                filterOptions={filterOptions}
                onFilterChange={onFilterChange}
                pinnedFilterIds={pinnedFilterIds}
                onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                searchText={searchText}
                onSearchTextChange={onSearchTextChange}
                hasActiveFilters={hasActiveFilters}
                onClearFilter={clearFilter}
                mode={mode}
                onModeChange={onModeChange}
                onOpenHistory={onOpenSnapshotDialog}
                isAsOf={isAsOf}
                asOf={asOf}
                historyLink={historyLink}
                onReturnToToday={onReturnToToday}
            />

            <Box sx={styles.mainColumns}>
                <DotLegalPaper fullHeight>
                    <Box sx={styles.cardHeaderRow}>
                        <DotLegalHeader headerStyle="small">{translateString("riskMatrix")}</DotLegalHeader>
                    </Box>
                    {hasData && (
                        <RiskAreaMatrix
                            readOnly
                            scale={scale}
                            consequenceLevels={consequenceLevels}
                            probabilityLevels={probabilityLevels}
                            riskLevelIndicators={riskLevelIndicators}
                            riskLevels={riskLevels}
                            matrix={matrix}
                            cellBadges={cellCounts}
                            cellMovements={cellMovements}
                            selectedConsequence={selectedCell?.consequence ?? null}
                            selectedProbability={selectedCell?.probability ?? null}
                            onCellClick={onCellClick}
                        />
                    )}
                </DotLegalPaper>

                <DotLegalPaper fullHeight>
                    <Box sx={styles.cardHeaderRow}>
                        <DotLegalHeader headerStyle="small">{translateString("riskGovernanceDistributionHistory")}</DotLegalHeader>
                    </Box>
                    <RiskDistributionHistoryChart
                        isLoading={distributionHistoryLoading}
                        history={distributionHistory}
                        riskLevels={riskLevels}
                        riskLevelIndicators={riskLevelIndicators}
                        asOf={asOf}
                    />
                </DotLegalPaper>
            </Box>

            {selectedCell && (
                <DotLegalPaper>
                    <Box sx={styles.cardHeaderRow}>
                        <DotLegalHeader headerStyle="small">
                            {`${translateString("riskGovernanceAssessedInCell")}: ${selectedCellLabel} (${cellEntities.length})`}
                        </DotLegalHeader>
                    </Box>
                    {cellEntities.length > 0 ? (
                        <RiskGovernanceEntityTable
                            rows={cellEntities}
                            isLoading={isLoading}
                            getRiskChipStyle={getRiskChipStyle}
                            onNavigate={navigateToEntity}
                            getEntityUrl={getEntityUrl}
                        />
                    ) : (
                        <Box sx={styles.cellEmptyStateWrapper}>
                            <DotLegalEmptyState noFixedHeight text={translateString("noData")} />
                        </Box>
                    )}
                    <Box sx={styles.clearFilterRow}>
                        <DotLegalButton buttonType="secondary" onClick={clearCellFilter}>
                            {translateString("riskGovernanceClearMatrixFilter")}
                        </DotLegalButton>
                    </Box>
                </DotLegalPaper>
            )}

            {snapshotDialogOpen && (
                <RiskSnapshotDialog
                    isLoading={snapshotDatesLoading}
                    dates={snapshotDates}
                    onSelectDate={onSelectSnapshotDate}
                    onClose={onCloseSnapshotDialog}
                />
            )}
        </Box>
    );
}

export default RiskGovernanceOverview;
